import { configurationQueryOptions } from '~entities/configuration/api/configuration.queries';
import { Offering } from '../model/offering.types';
import { queryClient } from '~shared/query-client';
import i18n, { Locale } from '~app/i18n';

/**
 * Retrieves an offering by its ID from the cached configuration data.
 *
 * This function searches through all available offerings including rooms,
 * packages from package categories, and last minute packages to find
 * an offering with the specified ID.
 *
 * @param id - The unique identifier of the offering to find
 * @returns The offering object if found, undefined otherwise
 *
 * @example
 * ```typescript
 * const offering = getOfferingById('room-123');
 * if (offering) {
 *   log('Found offering:', offering.name);
 * }
 * ```
 */
export const getOfferingById = (id?: Offering['id']): Offering | undefined => {
  if (!id) return;

  const configuration = queryClient.getQueryData(
    configurationQueryOptions(i18n.language as Locale).queryKey,
  );

  if (!configuration) return;

  const { rooms, packageCategories, lastMinutePackages } = configuration;

  const packages = packageCategories.flatMap(
    packageCategory => packageCategory.packages,
  );

  return [...rooms, ...packages, ...lastMinutePackages].find(
    offering => offering.id === id,
  );
};
